<template>
    <div class="stake-liquid-compare">
        <!-- Wallet not connected -->
        <div v-if="!walletConnected" class="text-muted small py-3">
            Connect your wallet to see your LST balances.
        </div>

        <!-- Loading -->
        <div v-else-if="loading" class="text-center py-4">
            <div class="spinner-border spinner-border-sm text-primary"></div>
            <p class="small text-muted mt-2">Loading balances...</p>
        </div>

        <!-- Balances -->
        <div v-else>
            <div v-if="balances.length === 0" class="text-muted small py-3">
                No LST holdings found. Stake SOL for compassSOL to get started.
            </div>

            <div v-for="bal in balances" :key="bal.mint" class="d-flex align-items-center justify-content-between py-2 border-bottom">
                <div class="d-flex align-items-center gap-2">
                    <img v-if="bal.logo" :src="bal.logo" :alt="bal.name" style="width:24px;height:24px;border-radius:50%">
                    <div>
                        <div class="fw-bold small">{{ bal.symbol || bal.name }}</div>
                        <div class="text-muted" style="font-size: 0.75rem;">
                            APY: <span class="font-mono-num pct-change pct-positive">{{ bal.apy || '—' }}%</span>
                        </div>
                    </div>
                </div>
                <div class="text-end">
                    <div class="font-mono-num fw-bold">{{ formatAmount(bal.amount) }}</div>
                    <div class="text-muted small font-mono-num">
                        ~{{ formatAmount(bal.amount * (bal.tokenValue || 1)) }} SOL
                    </div>
                </div>
            </div>

            <!-- Total SOL value -->
            <div v-if="balances.length > 0" class="d-flex justify-content-between pt-3 fw-bold">
                <span>Total LST Value</span>
                <span class="font-mono-num">{{ formatAmount(totalSolValue) }} SOL</span>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'StakeLiquidCompare',
    props: {
        lstPools: { type: Array, default: () => [] },
        compasssolMint: { type: String, default: '' },
    },
    data() {
        return {
            loading: false,
            balances: [],
        };
    },
    computed: {
        walletConnected() {
            return !!window.wallet?.publicKey?.value;
        },
        totalSolValue() {
            return this.balances.reduce((sum, b) => sum + (b.amount * (b.tokenValue || 1)), 0);
        },
    },
    mounted() {
        if (this.walletConnected) this.loadBalances();
        this._walletInterval = setInterval(() => {
            if (this.walletConnected && this.balances.length === 0 && !this.loading) {
                this.loadBalances();
            }
        }, 2000);
    },
    beforeUnmount() {
        if (this._walletInterval) clearInterval(this._walletInterval);
    },
    methods: {
        formatAmount(val) {
            if (!val) return '0';
            return val.toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 });
        },

        async loadBalances() {
            if (!this.walletConnected) return;
            this.loading = true;
            try {
                const { Connection, PublicKey } = await import('@solana/web3.js');
                const { TOKEN_PROGRAM_ID } = await import('@solana/spl-token');
                const connection = new Connection(window.swapConfig?.rpcEndpoint || 'https://api.mainnet-beta.solana.com');
                const walletPk = new PublicKey(window.wallet.publicKey.value.toBase58());

                const accounts = await connection.getParsedTokenAccountsByOwner(walletPk, {
                    programId: TOKEN_PROGRAM_ID,
                });

                // Build a lookup of pool mints from SSR data
                const poolLookup = {};
                for (const pool of this.lstPools) {
                    if (pool.poolMint) {
                        poolLookup[pool.poolMint] = pool;
                    }
                }

                const results = [];
                for (const { account } of accounts.value) {
                    const info = account.data.parsed?.info;
                    if (!info) continue;
                    const mint = info.mint;
                    const pool = poolLookup[mint];
                    if (!pool) continue;

                    const amount = parseFloat(info.tokenAmount?.uiAmountString || '0');
                    if (amount <= 0) continue;

                    results.push({
                        mint,
                        name: pool.name,
                        symbol: pool.symbol,
                        logo: pool.logo,
                        apy: pool.apy,
                        tokenValue: pool.poolTokenValue || 1,
                        amount,
                        isCompass: pool.isCompass,
                    });
                }

                // Sort: compassSOL first, then by value
                results.sort((a, b) => {
                    if (a.isCompass && !b.isCompass) return -1;
                    if (!a.isCompass && b.isCompass) return 1;
                    return (b.amount * b.tokenValue) - (a.amount * a.tokenValue);
                });

                this.balances = results;
            } catch (e) {
                console.error('[StakeLiquidCompare] Failed to load balances:', e);
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>
